import {
    Autocomplete, Box, Chip, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography
} from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { RemoveCircleOutline, GroupsOutlined } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { HttpEtapaEnvioMail } from './api'
import { EtapaEnvioViewDTO, EtapaEnvioFields } from './types'
import { HttpAxiosRequest } from 'src/http/httpAxiosBase'

type Props = {
    etapa: EtapaEnvioViewDTO
    allRows: EtapaEnvioViewDTO[]
    onClose: () => void
}

type GrupoOption = { id: number; descripcion: string }

function EditGruposEtapa({ etapa, allRows, onClose }: Props) {
    const [gruposAsignados, setGruposAsignados] = useState<GrupoOption[]>([])
    const [gruposDisponibles, setGruposDisponibles] = useState<GrupoOption[]>([])
    const [nuevoGrupo, setNuevoGrupo] = useState<GrupoOption | null>(null)

    useEffect(() => {
        // Grupos ya asignados a esta etapa/sistema
        const asignados = allRows
            .filter(r => r[EtapaEnvioFields.CodSistema] === etapa[EtapaEnvioFields.CodSistema]
                && r[EtapaEnvioFields.IdEtapa] === etapa[EtapaEnvioFields.IdEtapa]
                && r[EtapaEnvioFields.IdGrupo] !== null)
            .map(r => ({ id: r[EtapaEnvioFields.IdGrupo]!, descripcion: r[EtapaEnvioFields.NombreGrupo]! }))
        setGruposAsignados(asignados)
    }, [etapa, allRows])

    // Cargar grupos disponibles del sistema
    useEffect(() => {
        const codSistema = etapa[EtapaEnvioFields.CodSistema]
        HttpAxiosRequest.get(`cache/grupos/${codSistema}`).then((data: any) => {
            const lista = (data || []).map((g: any) => ({
                id: g.id,
                descripcion: g.descripcion || ''
            })).sort((a: any, b: any) => a.descripcion.localeCompare(b.descripcion))
            setGruposDisponibles(lista)
        }).catch(() => setGruposDisponibles([]))
    }, [etapa])

    const handleRemove = (idGrupo: number) => {
        setGruposAsignados(prev => prev.filter(g => g.id !== idGrupo))
    }

    const handleAdd = () => {
        if (!nuevoGrupo) return
        if (gruposAsignados.some(g => g.id === nuevoGrupo.id)) {
            toast.error('El grupo ya está asignado')
            return
        }
        setGruposAsignados(prev => [...prev, nuevoGrupo])
        setNuevoGrupo(null)
    }

    const handleSave = () => {
        const idsGrupo = gruposAsignados.map(g => g.id)
        const promise = HttpEtapaEnvioMail.updateGrupos(
            etapa[EtapaEnvioFields.CodSistema],
            etapa[EtapaEnvioFields.IdEtapa],
            idsGrupo
        ).then(() => { setTimeout(onClose, 500) })

        toast.promise(promise, { success: 'Grupos actualizados', error: 'Error al guardar', loading: 'Guardando' })
        return promise
    }

    const columnas: GridColDef[] = [
        { headerName: 'Grupo', field: 'descripcion', flex: 3 },
        {
            field: 'actions', type: 'actions', width: 60,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={<Tooltip title='Quitar'><RemoveCircleOutline fontSize='small' /></Tooltip>}
                    label='Quitar' onClick={() => handleRemove(row.id)} color='error'
                />
            ]
        }
    ]

    return (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <GroupsOutlined color='primary' />
                    <Typography variant='h6' fontWeight={600}>
                        Grupos de envío - {etapa[EtapaEnvioFields.NombreMostrar]}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {/* Agregar grupo */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, p: 2, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>Agregar:</Typography>
                    <Autocomplete
                        options={gruposDisponibles.filter(g => !gruposAsignados.some(a => a.id === g.id))}
                        value={nuevoGrupo}
                        onChange={(_, v) => setNuevoGrupo(v)}
                        getOptionLabel={(o) => o.descripcion}
                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                        size='small' sx={{ flex: 1 }}
                        renderInput={params => <TextField {...params} label='Grupo' />}
                    />
                    <Button variant='outlined' color='primary' size='small' onClick={handleAdd} disabled={!nuevoGrupo}>
                        Agregar
                    </Button>
                </Box>

                <DataGrid
                    disableColumnFilter disableColumnMenu
                    columns={columnas} rows={gruposAsignados}
                    density='compact' hideFooterPagination
                    autoHeight
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant='text' color='inherit' onClick={onClose}>Cancelar</Button>
                <Button variant='contained' color='primary' promise={handleSave}>Guardar</Button>
            </DialogActions>
        </>
    )
}

export default EditGruposEtapa
